import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * FORWARDS ITS REF, and that is load-bearing rather than tidy.
 *
 * The kit ships this as a plain function component, which is correct on React
 * 19 where `ref` is an ordinary prop. This console is on **React 18.3**, where
 * a function component silently drops the `ref` it is given — so every
 * `<Input ref={searchBox}>` in the codebase left `searchBox.current` null, and
 * the `/` shortcut that `lib/shortcuts.ts` binds on four screens had nothing to
 * focus. The binding existed, the keystroke did nothing, and no test could see
 * the difference because the hook itself was fine.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  function Input({ className, type, ...props }, ref) {
    return (
      <input
        type={type}
        ref={ref}
        data-slot="input"
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className
        )}
        {...props}
      />
    )
  },
)

export { Input }
